import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiX, FiCheck, FiLock, FiLoader } from 'react-icons/fi';
import { toast } from '../../utils/customToast';
import { useAuth } from '../../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import DowngradeConfirmModal from './DowngradeConfirmModal';

const PricingModal = ({ open, onClose, currentPlan, tenantId, onSuccess, onSelect }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upgrading, setUpgrading] = useState(false);
    // Billing interval: false = monthly, true = annual
    const [billingAnnual, setBillingAnnual] = useState(false);
    const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
    const { getAuthHeaders, activeTenantId, refreshUser } = useAuth();

    // Accessibility: refs for focus trap
    const overlayRef = useRef(null);
    const modalRef = useRef(null);
    const previouslyFocusedElement = useRef(null);

    // Si le modal s'ouvre et aucun tenant actif, tenter un refresh
    useEffect(() => {
        if (open && !tenantId && !activeTenantId && typeof refreshUser === 'function') {
            refreshUser();
        }
    }, [open, tenantId, activeTenantId, refreshUser]);

    // Only initialize Stripe if a publishable key is configured to avoid runtime errors
    const _stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    // Helper to get Stripe instance only when needed (for paid plans)
    const getStripe = async () => {
        if (!_stripePublishableKey) {
            throw new Error('Clé Stripe non configurée');
        }
        try {
            return await loadStripe(_stripePublishableKey);
        } catch (err) {
            console.warn('Stripe loading blocked (ad blocker?)', err);
            throw new Error('Impossible de charger Stripe. Veuillez désactiver votre bloqueur de publicités.');
        }
    };

    async function startCheckout({ priceId, interval = 'monthly', tenantId, authHeaders = {} }) {
        try {
            const effectiveTenantId = tenantId || activeTenantId;
            if (!effectiveTenantId) {
                throw new Error('Aucun tenant actif disponible. Veuillez rejoindre ou créer une organisation ou rafraîchir la page.');
            }
            const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/stripe/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({
                    priceId,
                    interval,
                    tenantId: effectiveTenantId
                })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || 'Impossible de créer la session');
            }

            if (data.url) {
                window.location.href = data.url;
                return;
            }

            if (data.sessionId) {
                const stripe = await getStripe();
                const result = await stripe.redirectToCheckout({ sessionId: data.sessionId });
                if (result && result.error) {
                    console.error(result.error.message);
                    // Only show one toast for error
                    toast.error(result.error.message);
                }
                return;
            }

            if (data.id) {
                const stripe = await getStripe();
                await stripe.redirectToCheckout({ sessionId: data.id });
                return;
            }
        } catch (err) {
            // Only show one toast for error
            if (err && err.message) {
                toast.error(err.message);
            } else {
                toast.error('Erreur lors de l\'initialisation du paiement');
            }
        }
    }

    const fetchPlans = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/plans`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                setPlans(data);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    // Scroll lock + focus trap + restore focus
    useEffect(() => {
        if (open) {
            fetchPlans();
            previouslyFocusedElement.current = document.activeElement;
            document.body.style.overflow = 'hidden';
            setTimeout(() => {
                if (modalRef.current) {
                    const focusableEls = modalRef.current.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                    );
                    if (focusableEls.length) {
                        focusableEls[0].focus();
                    } else {
                        modalRef.current.focus();
                    }
                }
            }, 10);
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    onClose && onClose();
                } else if (e.key === 'Tab') {
                    if (!modalRef.current) return;
                    const focusableEls = modalRef.current.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                    );
                    if (!focusableEls.length) return;
                    const first = focusableEls[0];
                    const last = focusableEls[focusableEls.length - 1];
                    if (e.shiftKey) {
                        if (document.activeElement === first) {
                            e.preventDefault();
                            last.focus();
                        }
                    } else {
                        if (document.activeElement === last) {
                            e.preventDefault();
                            first.focus();
                        }
                    }
                }
            };
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
                if (previouslyFocusedElement.current && previouslyFocusedElement.current.focus) {
                    previouslyFocusedElement.current.focus();
                }
            };
        } else {
            document.body.style.overflow = '';
        }
    }, [open, fetchPlans, onClose]);

    // Improved Billing toggle with clear labels and animated knob
    const BillingToggle = () => {
        const toggleSwitch = () => setBillingAnnual((v) => !v);

        return (
            <div className="ml-0 sm:ml-4 flex items-center gap-2 select-none">
                <div className="text-sm text-gray-600 dark:text-gray-300 font-medium">Mensuel</div>
                <button
                    onClick={toggleSwitch}
                    role="switch"
                    aria-checked={billingAnnual}
                    aria-label={billingAnnual ? 'Facturation annuelle' : 'Facturation mensuelle'}
                    className="relative w-12 h-6 rounded-full p-0.5 transition-colors focus:outline-none"
                    style={{
                        background: billingAnnual ? 'rgba(16,185,129,0.2)' : 'rgba(55,65,81,0.1)'
                    }}
                >
                    <div
                        className="w-5 h-5 rounded-full shadow-sm transition-all duration-300 ease-in-out"
                        style={{
                            background: billingAnnual ? '#10b981' : '#374151',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transform: billingAnnual ? 'translateX(24px)' : 'translateX(0px)'
                        }}
                    />
                </button>
                <div className="text-sm text-gray-600 dark:text-gray-300 font-medium">Annuel</div>
            </div>
        );
    };

    // Animated numeric price component: show a discrete sequence of intermediate values
    // to mimic a counting/jump animation (e.g., 15, 23, 34, ... 99)
    const AnimatedPrice = ({ value = 0, suffix = '€', duration = 700 }) => {
        const [display, setDisplay] = useState(() => Number(value));
        const [highlight, setHighlight] = useState('none'); // 'up'|'down'|'none'
        const intervalRef = useRef(null);
        const fromRef = useRef(Number(value));

        const generateSequence = (from, to, steps = 10) => {
            const seq = [];
            for (let i = 1; i <= steps; i++) {
                const t = i / steps;
                const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
                const v = from + (to - from) * eased;
                seq.push(v);
            }
            // Choose integer stepping when gap >= 1, otherwise keep two decimals
            const useInt = Math.abs(to - from) >= 1;
            const mapped = seq.map((v) => (useInt ? Math.round(v) : Number(v.toFixed(2))));
            // Remove consecutive duplicates
            const uniq = mapped.filter((v, i) => i === 0 || v !== mapped[i - 1]);
            const final = useInt ? Math.round(to) : Number(Number(to).toFixed(2));
            if (uniq.length === 0 || uniq[uniq.length - 1] !== final) uniq.push(final);
            return uniq;
        };

        useEffect(() => {
            const to = Number(value);
            const from = fromRef.current ?? to;
            if (to === from) return;
            setHighlight(to > from ? 'up' : 'down');
            const diff = Math.abs(to - from);
            const steps = Math.min(14, Math.max(6, Math.ceil(diff / (diff > 50 ? 8 : 6))));
            const seq = generateSequence(from, to, steps);
            let idx = 0;
            const per = Math.max(30, Math.floor(duration / seq.length));
            // clear any previous
            if (intervalRef.current) clearInterval(intervalRef.current);
            // start sequence
            setDisplay(seq[0]);
            intervalRef.current = setInterval(() => {
                idx += 1;
                if (idx < seq.length) {
                    setDisplay(seq[idx]);
                } else {
                    // end
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    // ensure final exact formatted value (two decimals)
                    setDisplay(Number(Number(to).toFixed(2)));
                    // clear highlight after short delay
                    setTimeout(() => setHighlight('none'), 300);
                    fromRef.current = to;
                }
            }, per);

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            };
        }, [value, duration]);

        const formatted = (n) => {
            if (n == null || Number.isNaN(n)) return '';
            // During sequence we may display integers, final is two decimals
            if (Number(n) === Math.round(n)) return `${Math.round(n)}`;
            return Number(n).toFixed(2);
        };

        const highlightClass = highlight === 'up' ? 'text-red-500' : highlight === 'down' ? 'text-green-500' : 'text-black dark:text-white';

        return (
            <div className="flex items-baseline gap-2">
                <div className={`text-2xl sm:text-2xl font-bold ${highlightClass} transition-colors`} style={{ lineHeight: 1 }}>
                    {formatted(display)}{suffix}
                </div>
            </div>
        );
    };

    // Price switcher wrapper using AnimatedPrice
    const PriceSwitcher = ({ monthly = 0, annual = null, billingAnnual = false }) => {
        const value = billingAnnual ? (annual ?? monthly * 12) : monthly;
        return (
            <div className="flex items-baseline gap-2">
                <AnimatedPrice value={value} suffix={'€'} duration={700} />
                <div className="text-xs sm:text-sm text-gray-500">{billingAnnual ? '/an' : '/mois'}</div>
            </div>
        );
    };

    // Fonction pour effectuer le downgrade après confirmation
    const performDowngrade = async () => {
        setUpgrading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/${tenantId}/downgrade-free`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                }
            });

            if (response.ok) {
                await response.json();
                toast.success('Votre abonnement a été annulé. Vous êtes maintenant sur le plan gratuit.');

                // Rafraîchir les données utilisateur
                if (refreshUser) {
                    await refreshUser();
                }

                setShowDowngradeConfirm(false);
                // Fermer le modal et notifier le parent
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                const error = await response.json();
                toast.error(error.message || 'Erreur lors de l\'annulation de l\'abonnement');
            }
        } catch (error) {
            console.error('Error downgrading to free:', error);
            toast.error('Erreur réseau lors de l\'annulation de l\'abonnement');
        } finally {
            setUpgrading(false);
        }
    };



    const handleUpgrade = async (planId) => {
        // Only consider "already on this plan" when we're upgrading an existing tenant
        if (tenantId && planId === currentPlan) {
            toast.info('Vous êtes déjà sur ce plan');
            return;
        }

        // Gestion du plan gratuit :
        if (planId === 'free') {
            if (tenantId) {
                if (currentPlan === 'free') {
                    toast.info('Vous êtes déjà sur le plan gratuit');
                    return;
                } else {
                    setShowDowngradeConfirm(true);
                    return;
                }
            }
            // Si pas de tenantId (création d'orga), laisser passer (onSelect ou suite du flow)
        }

        // Handle paid plans via Stripe when tenant exists
        if (planId !== 'free') {
            // If no tenantId (plan picker during creation), allow selection for later upgrade
            if (!tenantId || !getAuthHeaders) {
                if (onSelect) {
                    onSelect(planId);
                }
                return;
            }

            setUpgrading(true);
            try {
                // Fetch configured price ids from backend (per-plan)
                const pricesResp = await fetch(`${process.env.REACT_APP_API_URL}/stripe/plans`, {
                    headers: getAuthHeaders()
                });
                const pricesBody = pricesResp.ok ? await pricesResp.json() : {};

                // pricesBody expected shape: { plans: [{ id, name, monthlyPriceId, annualPriceId, ... }, ...] }
                const planEntry = (pricesBody.plans || []).find(p => p.id === planId);
                const priceId = planEntry
                    ? (billingAnnual ? (planEntry.annualPriceId || planEntry.monthlyPriceId) : (planEntry.monthlyPriceId || planEntry.annualPriceId))
                    : null;

                if (!priceId) {
                    toast.error('Configuration de paiement indisponible');
                    setUpgrading(false);
                    return;
                }

                // Stocker les informations de l'upgrade pour le traitement après paiement
                localStorage.setItem('pendingStripeUpgrade', JSON.stringify({
                    plan: planId,
                    tenantId: tenantId,
                    timestamp: Date.now()
                }));

                // Use the startCheckout function instead of duplicating code
                await startCheckout({
                    priceId,
                    interval: billingAnnual ? 'annual' : 'monthly',
                    tenantId,
                    authHeaders: getAuthHeaders()
                });
            } catch (error) {
                console.error('Stripe checkout error', error);
                toast.error('Erreur réseau lors de la création du paiement');
                // Nettoyer le localStorage en cas d'erreur
                localStorage.removeItem('pendingStripeUpgrade');
            } finally {
                setUpgrading(false);
            }
            return;
        }

        // If a selection callback is provided and no tenantId, treat this as a plan-picker for creation
        if (onSelect && !tenantId) {
            // En mode création, seul le plan gratuit est autorisé
            if (planId !== 'free') {
                toast.error('Seul le plan gratuit peut être sélectionné lors de la création. Vous pourrez upgrader après la création de votre organisation.');
                return;
            }

            try {
                onSelect(planId);
                onClose();
            } catch (err) {
                console.error('Error in onSelect callback:', err);
                toast.error('Erreur lors du traitement du plan');
            }
            return;
        }

        setUpgrading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants/${tenantId}/upgrade`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({ plan: planId })
                }
            );

            if (response.ok) {
                toast.success('Plan mis à jour avec succès !');
                onSuccess && onSuccess();
                onClose();
            } else {
                const error = await response.json();
                // Only show one error toast
                toast.error(error.message || 'Erreur lors de la mise à jour');
            }
        } catch (error) {
            toast.error('Erreur réseau');
        } finally {
            setUpgrading(false);
        }
    };


    // Find plan data for DowngradeConfirmModal (must be defined before return)
    const currentPlanData = plans.find(p => p.id === currentPlan);
    const freePlanData = plans.find(p => p.id === 'free');

    if (!open) return null;

    // Click outside to close
    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) {
            onClose && onClose();
        }
    };

    return (
        <>
            <DowngradeConfirmModal
                open={showDowngradeConfirm}
                onClose={() => setShowDowngradeConfirm(false)}
                onConfirm={performDowngrade}
                upgrading={upgrading}
                currentPlanData={currentPlanData}
                freePlanData={freePlanData}
            />

            <div
                ref={overlayRef}
                className="fixed inset-0 flex items-start sm:items-center justify-center bg-black bg-opacity-60 p-4 overflow-y-auto"
                aria-modal="true"
                role="dialog"
                tabIndex={-1}
                onClick={handleOverlayClick}
            >
                <div
                    ref={modalRef}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mb-4 mt-20 sm:my-8 outline-none"
                    role="document"
                    tabIndex={0}
                    aria-labelledby="pricing-modal-title"
                >
                    {/* Header - Sticky sur mobile */}
                    <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-6 border-b dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
                        <div className="flex-1 min-w-0">
                            <h2 id="pricing-modal-title" className="text-lg sm:text-2xl font-bold text-black dark:text-white truncate">
                                Choisir un plan
                            </h2>
                            {/* Billing interval toggle - mobile only */}
                            <div className="mt-2 sm:hidden w-full flex justify-start">
                                <BillingToggle />
                            </div>
                        </div>
                        {/* Billing interval toggle - desktop only */}
                        <div className="items-center hidden sm:flex">
                            <BillingToggle />
                        </div>
                        <button
                            onClick={onClose}
                            className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                            aria-label="Fermer"
                        >
                            <FiX className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                        </button>
                    </div>

                    {/* Content - Scrollable */}
                    {loading ? (
                        <div className="p-12 flex justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <div className="p-4 sm:p-6 max-h-[calc(100vh-20rem)] overflow-y-auto lg:min-h-[auto]">
                            {/* Définir isCreationMode au niveau du composant */}
                            {(() => {
                                const isCreationMode = !tenantId; // Mode création si pas de tenantId

                                return (
                                    <>
                                        {/* Grid responsive : 1 col sur mobile, 2 sur tablette, 5 sur desktop */}
                                        <div className="grid grid-cols-1 sm:md:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-2">
                                            {plans.map((plan) => {
                                                // When used as a plan-picker during tenant creation (no tenantId and onSelect provided),
                                                // we should NOT mark any plan as the "current" plan. Only consider currentPlan when
                                                // a tenantId is present (we're viewing/upgrading an existing tenant).
                                                const isCurrent = tenantId ? plan.id === currentPlan : false;
                                                const isPaid = plan.id !== 'free';
                                                const isDisabled = isCurrent || upgrading || (isCreationMode && isPaid);

                                                return (
                                                    <div
                                                        key={plan.id}
                                                        className={`border-2 rounded-lg p-4 sm:p-4 flex flex-col relative ${isCurrent
                                                                ? 'border-primary bg-primary/5'
                                                                : (isCreationMode && isPaid)
                                                                    ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 opacity-50'
                                                                    : 'border-gray-200 dark:border-gray-700'
                                                            }`}
                                                    >
                                                        {/* Badge "Plan actuel" */}
                                                        {isCurrent && (
                                                            <div className="absolute top-3 right-3 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full flex items-center gap-1">
                                                                <FiCheck className="h-3 w-3" aria-hidden="true" />
                                                                <span className="hidden sm:inline">Actuel</span>
                                                            </div>
                                                        )}

                                                        {/* Badge "Non disponible" pour les plans payants en mode création */}
                                                        {isCreationMode && isPaid && (
                                                            <div className="absolute top-3 right-3 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full flex items-center gap-1">
                                                                <FiLock className="h-3 w-3" aria-hidden="true" />
                                                                <span className="hidden sm:inline">Free</span>
                                                            </div>
                                                        )}

                                                        {/* Nom et Prix */}
                                                        <div className="mb-3 sm:mb-4">
                                                            <h3 className="text-lg sm:text-lg font-bold text-black dark:text-white mb-1 sm:mb-2">
                                                                {plan.name}
                                                            </h3>
                                                            <div>
                                                                {plan.price === 0 ? (
                                                                    <div className="text-2xl sm:text-xl font-bold text-black dark:text-white">Gratuit</div>
                                                                ) : (
                                                                    <PriceSwitcher monthly={plan.price} annual={plan.annualPrice} billingAnnual={billingAnnual} />
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Features */}
                                                        <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-1">
                                                            {plan.features.map((feature, idx) => (
                                                                <li
                                                                    key={idx}
                                                                    className="flex items-start gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300"
                                                                >
                                                                    <FiCheck className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                                                    <span>{feature}</span>
                                                                </li>
                                                            ))}
                                                        </ul>

                                                        {/* Bouton */}
                                                        <button
                                                            onClick={() => handleUpgrade(plan.id)}
                                                            disabled={isDisabled}
                                                            className={`w-full py-2 sm:py-3 px-3 sm:px-4 rounded-lg font-semibold text-sm sm:text-sm transition flex items-center justify-center gap-2 ${isCurrent
                                                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                                                    : (isCreationMode && isPaid)
                                                                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                                                        : upgrading
                                                                            ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                                                                            : 'bg-primary text-white dark:text-black hover:bg-primary-hover'
                                                                }`}
                                                        >
                                                            {isCurrent ? (
                                                                'Plan actuel'
                                                            ) : (isCreationMode && isPaid) ? (
                                                                <>
                                                                    <FiLock className="h-6 w-6" aria-hidden="true" />
                                                                    <span className='text-sm'>Choisissez Free puis un plan supérieur</span>
                                                                </>
                                                            ) : upgrading ? (
                                                                <>
                                                                    <FiLoader className="h-4 w-4 animate-spin" aria-hidden="true" />
                                                                    <span>Chargement...</span>
                                                                </>
                                                            ) : (
                                                                'Choisir ce plan'
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Note en bas */}
                                        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                            {isCreationMode ? (
                                                <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300 text-center">
                                                    🚀 <strong>Commencez avec le plan gratuit</strong> puis upgradez facilement vers un plan payant depuis les paramètres de votre organisation.
                                                </p>
                                            ) : (
                                                <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300 text-center">
                                                    💡 Les plans payants incluent des fonctionnalités avancées comme les exports/imports, les mentions utilisateurs, les notifications, le SSO et le support prioritaire.
                                                </p>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* Footer sticky avec bouton fermer sur mobile */}
                    <div className="sticky bottom-0 z-10 p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg sm:hidden">
                        <button
                            onClick={onClose}
                            className="w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-3 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PricingModal;